import { useCallback, useContext, useEffect, useRef } from 'react';
import { DndContext } from 'react-dnd';

const DEFAULT_ZONE_SIZE = 60;
const DEFAULT_MAX_SPEED = 15;

type UseAutoScrollOnDragOptions = {
  zoneSize?: number;
  maxSpeed?: number;
};

const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  let current: HTMLElement | null = element;

  while (current) {
    const { overflowY } = getComputedStyle(current);
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
};

export const useAutoScrollOnDrag = (
  options?: UseAutoScrollOnDragOptions
): React.RefCallback<HTMLElement> => {
  const zoneSize = options?.zoneSize ?? DEFAULT_ZONE_SIZE;
  const maxSpeed = options?.maxSpeed ?? DEFAULT_MAX_SPEED;

  const { dragDropManager } = useContext(DndContext);

  const containerRef = useRef<HTMLElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const clientOffsetRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!dragDropManager) {
      return;
    }

    const monitor = dragDropManager.getMonitor();

    const tick = () => {
      const element = containerRef.current;
      const offset = clientOffsetRef.current;

      if (!element || !offset || !isDraggingRef.current) {
        rafRef.current = null;
        return;
      }

      const container = getScrollParent(element);

      if (!container) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const rect = container.getBoundingClientRect();
      const cursorX = offset.x;
      const cursorY = offset.y;

      const isWithinHorizontalBounds =
        cursorX >= rect.left && cursorX <= rect.right;

      if (!isWithinHorizontalBounds) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const distFromTop = cursorY - rect.top;
      const distFromBottom = rect.bottom - cursorY;

      let scrollDelta = 0;

      if (distFromTop >= 0 && distFromTop < zoneSize) {
        const intensity = 1 - distFromTop / zoneSize;
        scrollDelta = -maxSpeed * intensity;
      } else if (distFromBottom >= 0 && distFromBottom < zoneSize) {
        const intensity = 1 - distFromBottom / zoneSize;
        scrollDelta = maxSpeed * intensity;
      }

      if (scrollDelta !== 0) {
        container.scrollTop += scrollDelta;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const startScrollLoop = () => {
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    const stopScrollLoop = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const unsubscribe = monitor.subscribeToStateChange(() => {
      isDraggingRef.current = monitor.isDragging();
      clientOffsetRef.current = monitor.getClientOffset();

      if (isDraggingRef.current) {
        startScrollLoop();
      } else {
        stopScrollLoop();
      }
    });

    const unsubscribeOffset = monitor.subscribeToOffsetChange(() => {
      clientOffsetRef.current = monitor.getClientOffset();
    });

    return () => {
      stopScrollLoop();
      unsubscribe();
      unsubscribeOffset();
    };
  }, [dragDropManager, zoneSize, maxSpeed]);

  const setRef = useCallback((element: HTMLElement | null) => {
    containerRef.current = element;
  }, []);

  return setRef;
};
